import React, { useState } from 'react';
import { PoemHighlight } from '@/types/highlights';
import { cn } from '@/lib/utils';
import { AlignJustify } from 'lucide-react';

interface HighlightNoteContentProps {
  highlight: PoemHighlight;
  className?: string;
  contentMode?: 'full' | 'label';
  isOverlapping?: boolean;
}

/**
 * Component to render rich content for highlight notes
 * Features:
 * - Shows display label as heading
 * - Shows formatted note text
 * - Includes multi-line indicator if applicable
 * - Supports expanding long notes
 * - Supports different display modes (full or label-only)
 */
export const HighlightNoteContent: React.FC<HighlightNoteContentProps> = ({ 
  highlight, 
  className,
  contentMode = 'full',
  isOverlapping
}) => {
  const [expanded, setExpanded] = useState(false);
  const isMultiLine = highlight.start_line_index !== highlight.end_line_index;
  const noteLength = highlight.note?.length || 0;
  const isLongNote = noteLength > 150;
  
  // Clean any "(part X of Y)" markers from the note
  const cleanNote = highlight.note?.replace(/\s*\(part \d+ of \d+\)\s*$/i, '') || '';

  // For label-only mode, show minimal content
  if (contentMode === 'label') {
    return (
      <div className={cn("py-0.5", className)}>
        <span className="font-medium text-foreground">
          {highlight.display_label || 'Highlight'}
        </span>
        {isMultiLine && (
          <span className="ml-2 text-xs text-muted-foreground">(Multi-line)</span>
        )}
      </div>
    );
  }
  
  // Full content mode with expandable note
  return (
    <div className={cn("grid gap-2", className)}>
      {/* Header with display label and multi-line indicator */}
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-medium text-foreground">
          {highlight.display_label || 'Highlight'}
        </h4>
        
        {isMultiLine && (
          <div 
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-muted-foreground bg-muted/50"
            title="This highlight spans multiple lines"
          >
            <AlignJustify className="w-3 h-3" />
            <span className="text-xs">Multi-line</span>
          </div>
        )}
      </div>
      
      {/* Revert: Restore original note rendering logic */}
      <div className="text-muted-foreground">
        {isLongNote && !expanded && isOverlapping ? (
          <>
            <p>{cleanNote.substring(0, 120)}...</p>
            <button 
              onClick={() => setExpanded(true)}
              className="text-xs text-primary hover:text-primary/80 mt-1 underline-offset-2 hover:underline"
            >
              Read more
            </button>
          </>
        ) : (
          <>
            <p>{cleanNote}</p>
            {isLongNote && expanded && (
              <button 
                onClick={() => setExpanded(false)}
                className="text-xs text-primary hover:text-primary/80 mt-1 underline-offset-2 hover:underline"
              >
                Show less
              </button>
            )}
          </>
        )}
      </div>
      
      {/* Color indicator */}
      <div 
        className="h-0.5 w-full rounded-full mt-1 opacity-70" 
        style={{ backgroundColor: highlight.color }}
      />
    </div>
  );
}; 